/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBell,
  faEnvelope,
  faChartLine,
  faCog,
  faUsers,
  faCheck,
  faCheckDouble,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import notificationApi from '../api/notificationApi';
import messageApi from '../api/messageApi';
import './UniversityDashboardHeader.css';

const AssociationDashboardHeader = ({
  currentPage = 'Dashboard'
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [notificationCount, setNotificationCount] = useState(0);
  const [messageCount, setMessageCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [messages, setMessages] = useState([]);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [associationLogo, setAssociationLogo] = useState(null);
  const [associationName, setAssociationName] = useState('');

  const notificationRef = useRef(null);
  const messageRef = useRef(null);
  const userMenuRef = useRef(null);

  useEffect(() => {
    fetchNotificationCount();
    fetchMessageCount();
    fetchProfilePhoto();
    fetchAssociationLogo();
    const interval = setInterval(() => {
      fetchNotificationCount();
      fetchMessageCount();
    }, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Refresh notifications when dropdown is opened
  useEffect(() => {
    if (showNotifications) {
      fetchNotificationCount();
    }
  }, [showNotifications]);

  // Refresh messages when dropdown is opened
  useEffect(() => {
    if (showMessages) {
      fetchMessageCount();
    }
  }, [showMessages]);

  const fetchProfilePhoto = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(buildApiUrl('/api/user/profile'), {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000
      });

      if (response.data) {
        const photoPath = response.data.profilePhotoPath || response.data.profileImage || response.data.profilePhoto;
        if (photoPath) {
          let normalizedPath = photoPath.replace(/\\/g, '/');
          let fullUrl = '';
          if (normalizedPath.startsWith('uploads/')) {
            const uploadPath = normalizedPath.replace('uploads/', '');
            fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
          } else if (normalizedPath.startsWith('/uploads/')) {
            const uploadPath = normalizedPath.replace('/uploads/', '');
            fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
          } else if (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://')) {
            fullUrl = normalizedPath;
          } else {
            fullUrl = buildApiUrl(`/api/users/uploads/${normalizedPath}`);
          }
          setProfilePhoto(fullUrl);
        }
      }
    } catch (error) {
      console.log('Error fetching profile photo:', error);
    }
  };

  const fetchAssociationLogo = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(buildApiUrl('/api/user/profile'), {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000
      });

      if (response.data) {
        // Get association/NGO name
        const name = response.data.associationName || response.data.ngoName || response.data.companyName || '';
        setAssociationName(name);

        // Get association/NGO logo
        const logoPath = response.data.associationLogo || response.data.ngoLogo || response.data.companyLogo;
        if (logoPath) {
          let normalizedPath = logoPath.replace(/\\/g, '/');
          let fullUrl = '';
          if (normalizedPath.startsWith('uploads/')) {
            const uploadPath = normalizedPath.replace('uploads/', '');
            fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
          } else if (normalizedPath.startsWith('/uploads/')) {
            const uploadPath = normalizedPath.replace('/uploads/', '');
            fullUrl = buildApiUrl(`/api/users/uploads/${uploadPath}`);
          } else if (normalizedPath.startsWith('http://') || normalizedPath.startsWith('https://')) {
            fullUrl = normalizedPath;
          } else {
            fullUrl = buildApiUrl(`/api/users/uploads/${normalizedPath}`);
          }
          setAssociationLogo(fullUrl);
        }
      }
    } catch (error) {
      console.log('Error fetching association logo:', error);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (messageRef.current && !messageRef.current.contains(event.target)) {
        setShowMessages(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotificationCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setNotificationCount(0);
        setNotifications([]);
        return;
      }

      const response = await notificationApi.getNotifications();
      if (response && response.success) {
        const notifs = response.notifications || [];
        // Filter out read notifications - only show unread ones
        const unreadNotifs = notifs.filter(n => !n.is_read && !n.read);
        setNotifications(unreadNotifs);
        setNotificationCount(unreadNotifs.length);
      } else {
        setNotificationCount(0);
        setNotifications([]);
      }
    } catch (error) {
      if (error.code !== 'ECONNABORTED' && error.response?.status !== 404) {
        console.log('Error fetching notifications:', error);
      }
      setNotificationCount(0);
      setNotifications([]);
    }
  };

  const fetchMessageCount = async () => {
    try {
      const token = localStorage.getItem('token');

      if (!token) {
        setMessageCount(0);
        setMessages([]);
        return;
      }

      const response = await messageApi.getMessages();
      if (response && response.success) {
        const msgs = response.messages || [];
        setMessages(msgs);
        const unreadCount = msgs.filter(m => !m.is_read && !m.read).length;
        setMessageCount(unreadCount);
      } else {
        setMessageCount(0);
        setMessages([]);
      }
    } catch (error) {
      if (error.code !== 'ECONNABORTED' && error.response?.status !== 404) {
        console.log('Error fetching messages:', error);
      }
      setMessageCount(0);
      setMessages([]);
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      const notificationId = notification._id || notification.id;
      if (notificationId && (!notification.is_read && !notification.read)) {
        await notificationApi.markAsRead(notificationId);
        // Remove the notification from the list since it's now read
        setNotifications(prev => 
          prev.filter(n => (n._id !== notificationId && n.id !== notificationId))
        );
        setNotificationCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMessageClick = async (message) => {
    try {
      const messageId = message._id || message.id;
      if (messageId && (!message.is_read && !message.read)) {
        await messageApi.markAsRead(messageId);
        // Update local state
        setMessages(prev => 
          prev.map(m => 
            (m._id === messageId || m.id === messageId) 
              ? { ...m, is_read: true, read: true }
              : m
          )
        );
        setMessageCount(prev => Math.max(0, prev - 1));
      }
      navigate('/messages');
    } catch (error) {
      console.error('Error marking message as read:', error);
      navigate('/messages');
    }
  };

  const handleMarkAllNotificationsRead = async () => {
    try {
      await notificationApi.markAllAsRead();
      // Clear all notifications from the list since they're all read now
      setNotifications([]);
      setNotificationCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getUserInitials = () => {
    if (!user) return 'A';
    const firstName = user.firstName || user.first_name || '';
    const lastName = user.lastName || user.last_name || '';
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'A';
  };

  const getUserName = () => {
    if (!user) return 'User';
    const firstName = user.firstName || user.first_name || '';
    const lastName = user.lastName || user.last_name || '';
    return `${firstName} ${lastName}`.trim() || 'User';
  };

  const getUserRole = () => {
    // Check if user is NGO or Association
    if (user?.role === 'ngo' || user?.userType === 'ngo') {
      return 'NGO Administrator';
    }
    return 'Association Administrator';
  };

  // Check if this is BAPS Africa account
  const isBapsAfrica = user?.email === 'spco@africa.baps.org';
  // Check if user is NGO
  const isNGO = user?.role === 'ngo' || user?.userType === 'ngo';
  const headerTitle = isBapsAfrica ? 'BAPS AFRICA' : (isNGO ? 'NGO HUB' : 'ASSOCIATION HUB');

  return (
    <header className="university-dashboard-header">
      <div className="university-header-left">
        <div className="university-header-logo">
          <div className="university-logo-icon">
            <FontAwesomeIcon icon={faUsers} />
          </div>
          <div className="university-logo-text">
            <div className="university-logo-name">{headerTitle}</div>
            <div className="university-logo-subtitle">Akshar Jobs</div>
          </div>
        </div>
      </div>

      <div className="university-header-right">
        <div className="university-header-icon-wrapper" ref={notificationRef}>
          <button
            className="university-header-icon-btn"
            onClick={() => {
              setShowNotifications(!showNotifications);
              setShowMessages(false);
              setShowUserMenu(false);
            }}
          >
            <FontAwesomeIcon icon={faBell} />
            {notificationCount > 0 && (
              <span className="university-icon-badge">{notificationCount}</span>
            )}
          </button>

          {showNotifications && (
            <div className="university-dropdown-menu university-notifications-dropdown">
              <div className="university-dropdown-header">
                <h4>Notifications</h4>
                {notificationCount > 0 && (
                  <button
                    onClick={handleMarkAllNotificationsRead}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#3b82f6',
                      cursor: 'pointer',
                      fontSize: '12px',
                      padding: '4px 8px'
                    }}
                    title="Mark all as read"
                  >
                    <FontAwesomeIcon icon={faCheckDouble} /> Mark all read
                  </button>
                )}
              </div>
              <div className="university-dropdown-content">
                {notifications.length > 0 ? (
                  <>
                    {notifications
                      .filter(notification => !notification.is_read && !notification.read) // Only show unread notifications
                      .slice(0, 10)
                      .map((notification, index) => (
                      <div
                        key={notification._id || notification.id || index}
                        className={`university-notification-item ${(!notification.is_read && !notification.read) ? 'unread' : ''}`}
                        onClick={() => handleNotificationClick(notification)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div style={{ flex: 1 }}>
                          <p style={{ fontWeight: (!notification.is_read && !notification.read) ? 600 : 400, margin: 0 }}>
                            {notification.title || notification.message || 'New notification'}
                          </p>
                          {notification.message && notification.title && (
                            <p style={{ fontSize: '12px', color: '#64748b', margin: '4px 0 0 0' }}>
                              {notification.message}
                            </p>
                          )}
                          {notification.created_at && (
                            <p style={{ fontSize: '11px', color: '#9ca3af', margin: '4px 0 0 0' }}>
                              {new Date(notification.created_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                        {(!notification.is_read && !notification.read) && (
                          <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: '#3b82f6',
                            marginLeft: '8px'
                          }} />
                        )}
                      </div>
                    ))}
                    {notifications.length > 10 && (
                      <div
                        onClick={() => {
                          setShowNotifications(false);
                          navigate('/notifications');
                        }}
                        style={{
                          padding: '12px',
                          textAlign: 'center',
                          cursor: 'pointer',
                          borderTop: '1px solid #e5e7eb',
                          color: '#3b82f6',
                          fontWeight: 600
                        }}
                      >
                        View All Notifications ({notifications.length})
                      </div>
                    )}
                  </>
                ) : (
                  <div className="university-empty-dropdown">
                    <FontAwesomeIcon icon={faBell} />
                    <p>No new notifications</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="university-header-icon-wrapper" ref={messageRef}>
          <button
            className="university-header-icon-btn"
            onClick={() => {
              setShowMessages(!showMessages);
              setShowNotifications(false);
              setShowUserMenu(false);
            }}
          >
            <FontAwesomeIcon icon={faEnvelope} />
            {messageCount > 0 && (
              <span className="university-icon-badge">{messageCount}</span>
            )}
          </button>

          {showMessages && (
            <div className="university-dropdown-menu university-messages-dropdown">
              <div className="university-dropdown-header">
                <h4>Messages</h4>
                <button
                  onClick={() => {
                    setShowMessages(false);
                    navigate('/messages');
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#3b82f6',
                    cursor: 'pointer',
                    fontSize: '12px',
                    padding: '4px 8px'
                  }}
                >
                  View All
                </button>
              </div>
              <div className="university-dropdown-content">
                {messages.length > 0 ? (
                  <>
                    {messages.slice(0, 10).map((message, index) => (
                      <div
                        key={message._id || message.id || index}
                        className={`university-message-item ${(!message.is_read && !message.read) ? 'unread' : ''}`}
                        onClick={() => handleMessageClick(message)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div style={{ flex: 1 }}>
                          <p style={{ fontWeight: (!message.is_read && !message.read) ? 600 : 400, margin: 0 }}>
                            {message.sender_name || message.from_name || message.sender?.name || 'Unknown Sender'}
                          </p>
                          <p style={{ fontSize: '12px', color: '#64748b', margin: '4px 0 0 0' }}>
                            {message.content || message.message || 'New message'}
                          </p>
                          {message.created_at && (
                            <p style={{ fontSize: '11px', color: '#9ca3af', margin: '4px 0 0 0' }}>
                              {new Date(message.created_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                        {(!message.is_read && !message.read) && (
                          <div style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: '#3b82f6',
                            marginLeft: '8px'
                          }} />
                        )}
                      </div>
                    ))}
                    {messages.length > 10 && (
                      <div
                        onClick={() => {
                          setShowMessages(false);
                          navigate('/messages');
                        }}
                        style={{
                          padding: '12px',
                          textAlign: 'center',
                          cursor: 'pointer',
                          borderTop: '1px solid #e5e7eb',
                          color: '#3b82f6',
                          fontWeight: 600
                        }}
                      >
                        View All Messages ({messages.length})
                      </div>
                    )}
                  </>
                ) : (
                  <div className="university-empty-dropdown">
                    <FontAwesomeIcon icon={faEnvelope} />
                    <p>No new messages</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="university-header-user-wrapper" ref={userMenuRef}>
          <div
            className="university-header-user-info"
            onClick={() => {
              setShowUserMenu(!showUserMenu);
              setShowNotifications(false);
              setShowMessages(false);
            }}
          >
            <div className="university-user-details">
              <div className="university-user-name">{associationName || getUserName()}</div>
              <div className="university-user-role">
                {getUserRole()}
              </div>
            </div>
            <div className="university-user-avatar">
              {associationLogo ? (
                <img
                  src={associationLogo}
                  alt={associationName || 'Association Logo'}
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    objectFit: 'cover'
                  }}
                  onError={() => setAssociationLogo(null)}
                />
              ) : profilePhoto ? (
                <img
                  src={profilePhoto}
                  alt={getUserName()}
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    objectFit: 'cover'
                  }}
                  onError={() => setProfilePhoto(null)}
                />
              ) : (
                <div className="university-avatar-initials">{getUserInitials()}</div>
              )}
            </div>
          </div>

          {showUserMenu && (
            <div className="university-dropdown-menu university-user-dropdown">
              <div className="university-user-dropdown-header">
                {/* Name and role on the left */}
                <div>
                  <div className="university-dropdown-user-name">
                    {associationName || getUserName()}
                  </div>
                  <div className="university-dropdown-user-email">
                    {user?.email}
                  </div>
                </div>
                {/* Profile photo on the right */}
                <div className="university-user-avatar-large">
                  {associationLogo ? (
                    <img
                      src={associationLogo}
                      alt={associationName || 'Association Logo'}
                      style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                      onError={() => setAssociationLogo(null)}
                    />
                  ) : profilePhoto ? (
                    <img
                      src={profilePhoto}
                      alt={getUserName()}
                      style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                      onError={() => setProfilePhoto(null)}
                    />
                  ) : (
                    <div className="university-avatar-initials">
                      {getUserInitials()}
                    </div>
                  )}
                </div>
              </div>
              <div className="university-dropdown-divider"></div>
              <div className="university-dropdown-content">
                <div
                  className="university-dropdown-item"
                  onClick={() => {
                    // Redirect based on user role
                    if (user?.role === 'ngo' || user?.userType === 'ngo') {
                      navigate('/ngo-dashboard');
                    } else {
                      navigate('/association-dashboard');
                    }
                    setShowUserMenu(false);
                  }}
                >
                  <FontAwesomeIcon icon={faChartLine} />
                  <span>Dashboard</span>
                </div>
                <div 
                  className="university-dropdown-item" 
                  onClick={() => {
                    // Redirect based on user role
                    if (user?.role === 'ngo' || user?.userType === 'ngo') {
                      navigate('/ngo-dashboard?section=settings');
                    } else {
                      navigate('/association-dashboard?section=settings');
                    }
                    setShowUserMenu(false);
                  }}
                >
                  <FontAwesomeIcon icon={faCog} />
                  <span>Settings</span>
                </div>
                <div className="university-dropdown-divider"></div>
                <div
                  className="university-dropdown-item university-logout-item"
                  onClick={handleLogout}
                >
                  <FontAwesomeIcon icon={faCog} />
                  <span>Logout</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default AssociationDashboardHeader;


